import { useCallback, useRef } from 'react';
import { message } from 'antd';

export const BLOCKED_NAVIGATION_MESSAGE =
  'All panes are pinned. Unpin a pane to navigate.';

// A single blocked attempt stays silent; the message appears once the user
// keeps trying within a short window, then is suppressed for a cooldown.
const ATTEMPTS_BEFORE_NOTICE = 3;
const ATTEMPT_WINDOW_MS = 2000;
const NOTICE_COOLDOWN_MS = 5000;

/**
 * Returns a callback to record a navigation attempt that was blocked because
 * every visible pane is pinned. Shows one lightweight, non-modal message after
 * repeated attempts — never one per attempt.
 */
export function useBlockedNavigationNotice(): () => void {
  const attemptsRef = useRef<number[]>([]);
  const lastNoticeRef = useRef(-Infinity);

  return useCallback(() => {
    const now = Date.now();
    const attempts = attemptsRef.current.filter(
      (t) => now - t < ATTEMPT_WINDOW_MS
    );
    attempts.push(now);
    attemptsRef.current = attempts;

    if (attempts.length < ATTEMPTS_BEFORE_NOTICE) return;
    if (now - lastNoticeRef.current < NOTICE_COOLDOWN_MS) return;

    lastNoticeRef.current = now;
    attemptsRef.current = [];
    message.info({
      key: 'all-panes-pinned',
      content: BLOCKED_NAVIGATION_MESSAGE,
    });
  }, []);
}
